import { useEffect, useMemo, useState } from "react";
import { getUserType } from "util/auth";
import Notify from "config/notify";
import { DEFAULT_LIMIT_ITEMS } from "constants/data";
import { PaginationParamType } from "type";
import {
  TUploadImageType,
  WheelBrandDetailUpdateRequest,
  WheelBrandListItem,
  WheelItemPrice
} from "../types/wheel-brand";
import {
  deleteBrandWheelImage,
  getWheelDetails,
  updateWheelDetails,
  uploadBrandWheelImage,
} from "../api/wheelDetailsApi";

const parseToListItem = (wheelBrands: WheelItemPrice[]): WheelBrandListItem[] => {
  const userType = getUserType();
  return wheelBrands.map((brand) => {
    const fixedDiscountColor =
      userType === "C0002" && !brand.fixed_discount_store_login_edit_flag
        ? "var(--red-500)"
        : "var(--black)";
    const saleRateCostColor =
      userType === "C0002" && !brand.sale_price_rate_cost_store_login_edit_flag
        ? "var(--red-500)"
        : "var(--black)";

    return {
      ...brand,
      brand_cd: brand.brand_cd,
      maker_name: brand.maker_name || "",
      brand_name: brand.brand_name || "",
      fixed_discount: brand.fixed_discount == null ? "" : brand.fixed_discount.toString(),
      sale_price_rate_cost:
        brand.sale_price_rate_cost == null ? "" : brand.sale_price_rate_cost.toString(),
      fixed_discount_color: fixedDiscountColor,
      sale_price_rate_cost_color: saleRateCostColor,
      titleEditable: userType === "C0001" ? "true" : "false",
    } as never;
  });
};

/**
 *
 */
const useWheelBrand = () => {
  const [wheelBrandList, setWheelBrandList] = useState<WheelBrandListItem[]>([]);
  const [origialWheelBrandList, setOriginalWheelBrandList] = useState<WheelBrandListItem[]>([]);
  const [wheelItemPriceSelected, setWheelItemPriceSelected] = useState<WheelBrandListItem | null>(null);
  const [queryParams, setQueryParams] = useState<PaginationParamType>({
    limit: DEFAULT_LIMIT_ITEMS,
    offset: 1,
  });
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const totalPage = useMemo(() => Math.ceil(total / DEFAULT_LIMIT_ITEMS), [total]);

  const fetchData = async () => {
    setLoading(true);
    const data = await getWheelDetails(queryParams);
    if (data) {
      const parsedData = parseToListItem(data.list);
      setWheelBrandList((prev) => {
        if (queryParams.offset > 1) {
          return [...prev, ...parsedData];
        }
        return parsedData;
      });
      setOriginalWheelBrandList((prev) => {
        if (queryParams.offset > 1) {
          return [...prev, ...parsedData];
        }
        return parsedData;
      });
      setTotal(data.total);
    }
    setLoading(false);
  };

  const handleSelectBrand = (brandCd: string) => {
    const brand = wheelBrandList.find((item) => item.brand_cd === brandCd) || null;
    setWheelItemPriceSelected(brand);
  };

  const handleUpdateBrand = async (
    brandCd: string,
    key: keyof WheelBrandDetailUpdateRequest,
    data: string | number,
  ) => {
    const newBrands = [...wheelBrandList];
    const index = newBrands.findIndex((item) => item.brand_cd === brandCd);
    const item = origialWheelBrandList[index];
    if (index === -1) return;
    if (!item) return;

    const numericKeys = ["fixed_discount", "sale_price_rate_cost"];
    const isNumeric = numericKeys.includes(key);
    const originalValue = (item as unknown as Record<string, unknown>)[key];
    const isCleared =
      isNumeric &&
      data === "" &&
      originalValue !== "" &&
      originalValue !== null &&
      originalValue !== undefined;

    // For HQ users on /mst-wheel-price/setting, blurring a cleared numeric cell that
    // originally had a value is not allowed: show an error dialog and restore the previous
    // value instead of sending column_delete_flag. Originally-empty cells fall through to
    // the existing "no diff" early return below, so no dialog is shown for a no-op blur.
    if (isCleared) {
      const restoredValue = String(originalValue);
      setWheelBrandList((prev) => {
        const newList = [...prev];
        if (newList[index]) {
          newList[index] = {
            ...newList[index],
            [key]: restoredValue,
          } as never;
        }
        return newList;
      });
      Notify.setNotification({
        show: true,
        title: "値を入力してください",
        confirmLabel: "閉じる",
        actionsPosition: "center",
      });
      return;
    }

    if (originalValue === String(data)) return;

    try {
      const payload: WheelBrandDetailUpdateRequest = {
        brand_cd: brandCd,
        [key]: key === "sale_price_rate_cost" ? String(data) : data,
      };
      await updateWheelDetails(payload);
      setOriginalWheelBrandList((prev) => {
        const newOriginal = [...prev];
        (newOriginal[index] as unknown as Record<string, unknown>)[key] = String(data);
        return newOriginal;
      });
      setWheelBrandList((prev) => {
        const newList = [...prev];
        if (newList[index]) {
          newList[index] = {
            ...newList[index],
            [key]: String(data),
          } as never;
        }
        return newList;
      });
    } catch (error) {
      setWheelBrandList((prev) => {
        const prevBrand = [...prev];
        (prevBrand[index] as unknown as Record<string, unknown>)[key] = originalValue;
        return prevBrand;
      });
    }
  };
  
  const handleUploadImages = async (type: TUploadImageType, file: File, color?: string, hole?: string) => {
    if (wheelItemPriceSelected) {
      const response = await uploadBrandWheelImage(wheelItemPriceSelected.brand_cd, file, type, color, hole);
      if (response.status === 200) {
        Notify.setNotification({
          show: true,
          title: "アップロードが完了しました。",
          confirmLabel: "OK",
          actionsPosition: "center",
        });
        await fetchData();
      }
    }
  }

  const handleDeleteImage = async (type: 'main' | 'detail', color?: string, hole?: string) => {
    if (type === 'main' && !wheelItemPriceSelected?.wheel_image_url_main) return;
    
    Notify.setConfirm({
      show: true,
      title: "削除しますか？",
      actionsPosition: "center",
      onResult: async (result: boolean) => {
        if (result && wheelItemPriceSelected) {
          await deleteBrandWheelImage(wheelItemPriceSelected.brand_cd, type, color, hole);
          await fetchData();
        }
      },
    });
  };

  const onLoadMore = async () => {
    if (queryParams.offset + 1 > totalPage) return;
    setQueryParams((prev) => ({
      ...prev,
      offset: prev.offset + 1,
    }));
  };

  useEffect(() => {
    if (queryParams.offset > 1) {
      fetchData();
    }
  }, [queryParams.offset]);

  useEffect(() => {
    fetchData();
  }, []);

  // auto update wheelItemPriceSelected when wheelBrandList fetch is completed
  useEffect(() => {
    if (wheelItemPriceSelected && wheelBrandList.length > 0) {
      const updatedBrand = wheelBrandList.find(item => item.brand_cd === wheelItemPriceSelected.brand_cd);
      if (updatedBrand) {
        setWheelItemPriceSelected(updatedBrand);
      }
    }
  }, [wheelBrandList, wheelItemPriceSelected?.brand_cd]);

  return {
    wheelBrandList,
    handleSelectBrand,
    wheelItemPriceSelected,
    setWheelItemPriceSelected,
    handleUploadImages,
    handleUpdateBrand,
    handleDeleteImage,
    onLoadMore,
    loading,
  };
};

export default useWheelBrand;
